
import { debounce, generateUid } from "~/common/helpers";
import InteractiveGalleryItem from "./interactive-gallery-item.vue";

export default {
  components: { InteractiveGalleryItem },
  props: {
    /**
     * @typedef {Object} Photo
     * @property {number} id
     * @property {string} src
     * @property {string} status
     * @property {string} created_at
     * @property {Object} grid_data - grid item data for vue-grid-layout
     * @property {number} grid_data.x
     * @property {number} grid_data.y
     * @property {number} grid_data.w
     * @property {number} grid_data.h
     * @property {number} grid_data.i
     */

    /**
     * Array of photos
     * @type {Photo[]}
     */
    photos: {
      type: Array,
      default: () => [],
    },
    /**
     * Allow to move and resize the grid items
     */
    editable: {
      type: Boolean,
      default: false,
    },
    /**
     * Allow to show the photo info: status, created_at
     */
    readable: {
      type: Boolean,
      default: false,
    },
    /**
     * Allow to check the photo, then move it to another album or delete
     */
    checkable: {
      type: Boolean,
      default: false,
    },

    /* for alt text */
    alt: {
      type: String,
      default: "",
    },

    title: {
      type: String,
      default: "",
    },

    galleryType: {
      type: String,
      default: "",
    },

    /**
     * Allow to use complementary images from another album
     */
    withComplement: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["layout-updated", "photos-checked"],
  data() {
    return {
      layout: [], // for approved photos
      readyLayout: [], // init approved photos layout for detecting changes
      colCount: 4,
      rowHeight: 200,
      colWidth: 200,
      selectedPhotos: [],
      margin: [24, 24],
    };
  },
  computed: {
    params() {
      return this.$route.params;
    },
    isLayoutChanged() {
      return (
        this.readyLayout !==
        JSON.stringify(
          this.layout.map((item) => {
            return {
              x: item.x,
              y: item.y,
              w: item.w,
              h: item.h,
            };
          })
        )
      );
    },
    isTrackChanges() {
      return this.editable;
    },
  },
  watch: {
    photos: {
      handler: function () {
        this.processPhotos();
      },
      deep: true,
      immediate: true,
    },
    isLayoutChanged() {
      this.$emit("layout-updated", this.isLayoutChanged);
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.calculateCellSize();
    });

    window.addEventListener(
      "resize",
      debounce(() => this.calculateCellSize(), 100)
    );
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.calculateCellSize);
  },
  methods: {
    /**
     * @description Process photos objects to fit the grid items
     */
    processPhotos() {
      if (this.photos?.length) {
        this.layout = [];
        this.photos.reduce((acc, photo, i) => {
          const grid_item = {
            id: photo.id,
            i: generateUid(),
            src: photo.src,
            createdAt: photo.created_at,
            status: photo.status,
          };

          // we need add static = false only for editable grid
          // no need add static key if !this.editable
          if (this.editable) {
            grid_item.static = false;
          }

          // if grid data is already set, use it
          if (Object.keys(photo?.grid_data || {}).length) {
            grid_item.x = photo.grid_data.x;
            grid_item.y = photo.grid_data.y;
            grid_item.w = photo.grid_data.w;
            grid_item.h = photo.grid_data.h;
          }
          // otherwise, calculate the grid data
          else {
            grid_item.w = 1;
            grid_item.h = 1;
            grid_item.x = i % this.colCount;
            grid_item.y = Math.floor(i / this.colCount + 1);
          }

          acc.push(grid_item);
          return acc;
        }, this.layout);
      } else {
        this.layout = [];
      }
    },
    /**
     * @description Calculate the size of the grid items
     */
    calculateCellSize() {
      if (!this.$refs?.grid?.$el) return;
      this.colWidth = this.$refs.grid.$el.clientWidth / this.colCount;
      this.rowHeight = this.colWidth;
      this.margin = window.innerWidth < 992 ? [11, 11] : [24, 24];
      this.$nextTick(() => {
        this.$refs.grid.updateHeight();
      });
    },
    processHandles() {
      const resizeHandleSelector = ".vue-resizable-handle";
      const resizeHandles = this.$el.querySelectorAll(resizeHandleSelector);

      const dragHandleSelector = ".vue-draggable-handle";
      const dragHandles = this.$el.querySelectorAll(dragHandleSelector);

      [...resizeHandles, ...dragHandles].forEach((handle) => {
        handle.addEventListener(
          "touchmove",
          (e) => {
            e.preventDefault();
          },
          { passive: false }
        );
      });
    },

    layoutReadyEvent(newLayout) {
      this.processHandles();

      if (!this.isTrackChanges) return;
      this.readyLayout = JSON.stringify(
        newLayout.map((item) => {
          return {
            x: item.x,
            y: item.y,
            w: item.w,
            h: item.h,
          };
        })
      );
    },

    async saveLayout() {
      try {
        await this.$axios.post("/api/profiles/photo/order", {
          profile_id: this.$store.state.profiles.currentProfileForAdvertiser.id,
          data: this.layout.map((photo) => {
            return {
              id: photo.id,
              order: photo.y * this.colCount + photo.x + 1,
              grid_data: {
                x: photo.x,
                y: photo.y,
                w: photo.w,
                h: photo.h,
              },
            };
          }),
        });
        if (this.isTrackChanges) {
          this.readyLayout = JSON.stringify(
            this.layout.map((item) => {
              return {
                x: item.x,
                y: item.y,
                w: item.w,
                h: item.h,
              };
            })
          );
        }
        this.$store.commit(
          "profiles/setCurrentProfileForAdvertiserPhotosGridData",
          this.layout
        );
      } catch (e) {
        console.log(e);
      }
    },

    checkChanged(isChecked, photo) {
      if (isChecked) {
        this.selectedPhotos.push(photo);
      } else {
        this.selectedPhotos = this.selectedPhotos.filter(
          (item) => item.id !== photo.id
        );
      }
      this.$emit("photos-checked", this.selectedPhotos);
    },

    clearSelections() {
      this.selectedPhotos = [];
      this.$emit("photos-checked", this.selectedPhotos);
    },

    imgAlt(index) {
      return this.alt + ` - Photo ${index + 1} of ${this.photos.length}`;
    },

    imgTitle(index) {
      return this.title + ` - Photo ${index + 1} of ${this.photos.length}`;
    },
  },
};
